import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import {
  LabelCreatedEventSchema,
  LabelUpdatedEventSchema,
  LabelDeletedEventSchema,
  type LabelsState,
  LabelAssignmentStartedEventSchema,
} from 'apps/chat/interfaces/labels'
import { alert } from 'common/core'
import { chatApi } from 'shared/api/requests/chat'
import type { FilterDataType } from 'shared/api/requests/chat/filter/schemas'
import type { Label, LabelStrategy } from 'shared/api/requests/chat/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import {
  anotherTabNotificationsListener,
  userNotificationsListener,
} from 'utils/services/notificationsService'

export const insertLabelAfterMaxId = (labels: Label[], newLabel: Label): Label[] => {
  if (labels.length === 0) {
    return [newLabel]
  }

  const maxId = labels.reduce((acc: number, label: Label) => {
    return label.label_id > acc ? label.label_id : acc
  }, 0)

  const maxIdIndex = labels.findIndex((label) => label.label_id === maxId)

  if (maxIdIndex === -1) {
    return [...labels, newLabel]
  }

  return labels.toSpliced(maxIdIndex + 1, 0, newLabel)
}

const initialState: LabelsState = {
  assignmentIdsInProgress: [],
  error: null,
  isModalOpen: false,
  isFetching: false,
  isUpdating: false,
  isDeleting: false,
  isCreating: false,
  items: [],
  selectedLabelId: null,
  modalLabelId: null,
}

export const fetchLabels = createAsyncAction<Label[]>('chat/labels/fetch', async () => {
  const response = await chatApi.labels.fetch()
  return response.data.labels
})

export const createLabel = createAsyncAction<Label, Pick<Label, 'emoji' | 'name'>>(
  'chat/labels/create',
  async (data) => {
    const response = await chatApi.labels.create({
      body: data,
      query: { client_id: getClientId() },
    })

    alert(
      l.translate('Your label {labelName} was created successfully!', { labelName: data.name }),
      'success',
    )
    return response.data.label
  },
  {
    autoHandleByGuards: [
      chatApi.labels.create.isInvalidEmoji,
      chatApi.labels.create.isInvalidNameLength,
      chatApi.labels.create.isNameNotUnique,
    ],
  },
)

export const updateLabel = createAsyncAction<
  Label,
  Pick<Label, 'label_id' | 'emoji' | 'name'> & {
    strategy: LabelStrategy
    filter: Nullable<FilterDataType>
  }
>(
  'chat/labels/update',
  async (data) => {
    const response = await chatApi.labels.update({
      body: data,
      query: { client_id: getClientId() },
    })

    alert(
      l.translate('Your label {labelName} was updated successfully!', { labelName: data.name }),
      'success',
    )
    return response.data.label
  },
  {
    autoHandleByGuards: [
      chatApi.labels.update.isLabelNotFound,
      chatApi.labels.update.isInvalidEmoji,
      chatApi.labels.update.isInvalidNameLength,
      chatApi.labels.update.isNotCustomLabel,
      chatApi.labels.update.isNameNotUnique,
    ],
  },
)

export const deleteLabel = createAsyncAction<number, Pick<Label, 'label_id' | 'name'>>(
  'chat/labels/delete',
  async (data) => {
    await chatApi.labels.delete({
      body: { label_id: data.label_id },
      query: { client_id: getClientId() },
    })

    alert(
      l.translate('Your label {labelName} was deleted successfully!', { labelName: data.name }),
      'success',
    )
    return data.label_id
  },
  {
    autoHandleByGuards: [
      chatApi.labels.delete.isLabelNotFound,
      chatApi.labels.delete.isNotCustomLabel,
    ],
  },
)

const labelsSlice = createSlice({
  name: 'chat/labels',
  initialState,
  reducers: {
    setSelectedLabelId(state, action: PayloadAction<number | null>) {
      state.selectedLabelId = action.payload
    },
    openLabelsModal(state, action: PayloadAction<number | undefined>) {
      state.isModalOpen = true
      state.modalLabelId = action.payload || null
    },
    closeLabelsModal(state) {
      state.isModalOpen = false
      state.modalLabelId = null
    },
    addLabel(state, action: PayloadAction<Label>) {
      state.items = insertLabelAfterMaxId(state.items, action.payload)
    },
    updateLabelLocal(state, action: PayloadAction<Label>) {
      const updated = action.payload
      const idx = state.items.findIndex((label) => label.label_id === updated.label_id)
      if (idx !== -1) {
        state.items[idx] = updated
      } else {
        console.error('Label not found', updated)
      }
    },
    deleteLabelLocal(state, action: PayloadAction<number>) {
      const deletedId = action.payload
      state.items = state.items.filter((label) => label.label_id !== deletedId)
    },
    labelAssignmentFinished(state, action: PayloadAction<number>) {
      state.assignmentIdsInProgress = state.assignmentIdsInProgress.filter(
        (id) => id !== action.payload,
      )
    },
    labelAssignmentStarted(state, action: PayloadAction<number>) {
      state.assignmentIdsInProgress = [...state.assignmentIdsInProgress, action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabels.pending, (state) => {
        state.isFetching = true
        state.error = null
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.isFetching = false
        state.items = action.payload
        state.error = null
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.isFetching = false
        state.error = action.error.message || 'Failed to fetch labels'
      })
      .addCase(createLabel.pending, (state) => {
        state.isCreating = true
        state.error = null
      })
      .addCase(createLabel.fulfilled, (state, action) => {
        state.items = insertLabelAfterMaxId(state.items, action.payload)
        state.isCreating = false
      })
      .addCase(createLabel.rejected, (state, action) => {
        state.isCreating = false
        state.error = action.error.message || 'Failed to create label'
      })
      .addCase(updateLabel.pending, (state) => {
        state.isUpdating = true
        state.error = null
      })
      .addCase(updateLabel.fulfilled, (state, action) => {
        const updated = action.payload
        const idx = state.items.findIndex((label) => label.label_id === updated.label_id)
        if (idx !== -1) {
          state.items[idx] = updated
          state.isUpdating = false
        }
      })
      .addCase(updateLabel.rejected, (state, action) => {
        state.isUpdating = false
        state.error = action.error.message || 'Failed to update label'
      })
      .addCase(deleteLabel.pending, (state) => {
        state.isDeleting = true
        state.error = null
      })
      .addCase(deleteLabel.fulfilled, (state, action) => {
        const deletedId = action.payload
        state.items = state.items.filter((label) => label.label_id !== deletedId)
        state.isDeleting = false
      })
      .addCase(deleteLabel.rejected, (state, action) => {
        state.isDeleting = false
        state.error = action.error.message || 'Failed to delete label'
      })
  },
})

export const {
  setSelectedLabelId,
  openLabelsModal,
  closeLabelsModal,
  addLabel,
  updateLabelLocal,
  deleteLabelLocal,
  labelAssignmentFinished,
  labelAssignmentStarted,
} = labelsSlice.actions

export const { reducer } = labelsSlice

export const getLabels = (state: RootState) => state.chat.labels.items
export const getLabelsFetching = (state: RootState) => state.chat.labels.isFetching
export const getLabelsUpdating = (state: RootState) => state.chat.labels.isUpdating
export const getLabelsDeleting = (state: RootState) => state.chat.labels.isDeleting
export const getLabelsCreating = (state: RootState) => state.chat.labels.isCreating
export const getIsLabelsModalOpen = (state: RootState) => state.chat.labels.isModalOpen
export const getLabelsModalLabelId = (state: RootState) => state.chat.labels.modalLabelId
/** we agreed that system labels should have negative id and custom labels should have positive id */
export const getIsSystemLabelPresent = (state: RootState, labelId: number | null) =>
  Boolean(
    labelId && labelId < 0 && state.chat.labels.items.some((label) => label.label_id === labelId),
  )
export const getIsLabelAssignmentInProgress = (state: RootState, labelId: number) =>
  state.chat.labels.assignmentIdsInProgress.includes(labelId)
export const getLabelsAssignmentIdsInProgress = (state: RootState) =>
  state.chat.labels.assignmentIdsInProgress
export const getIsLabelsProcessing = (state: RootState) =>
  state.chat.labels.isUpdating || state.chat.labels.isDeleting || state.chat.labels.isCreating

anotherTabNotificationsListener.on(LabelCreatedEventSchema, (data, dispatch) => {
  dispatch(addLabel(data.model))
})
anotherTabNotificationsListener.on(LabelUpdatedEventSchema, (data, dispatch) => {
  dispatch(updateLabelLocal(data.model))
})
anotherTabNotificationsListener.on(LabelDeletedEventSchema, (data, dispatch) => {
  dispatch(deleteLabelLocal(data.id))
})

userNotificationsListener.on(LabelAssignmentStartedEventSchema, (data, dispatch) => {
  dispatch(labelAssignmentStarted(data.label_id))
})
